import { createContext, useCallback, useContext, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { AlertTriangle } from 'lucide-react'

// Native confirm() blocks the event loop, loses focus, can't be themed,
// has no a11y signal beyond the system beep, and looks like 2001. This
// gives callers the same one-line API (`await confirm(...)`) but renders
// as a Radix Dialog with focus trap, ESC close, and brand styling.

type ConfirmOpts = {
  title?: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  destructive?: boolean
}

type Resolver = (ok: boolean) => void

type ConfirmContextValue = (opts: ConfirmOpts) => Promise<boolean>

const ConfirmContext = createContext<ConfirmContextValue | null>(null)

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [opts, setOpts] = useState<ConfirmOpts | null>(null)
  const [resolver, setResolver] = useState<Resolver | null>(null)

  const confirm = useCallback<ConfirmContextValue>((o) => {
    setOpts(o)
    return new Promise<boolean>((resolve) => {
      setResolver(() => resolve)
    })
  }, [])

  function answer(ok: boolean) {
    resolver?.(ok)
    setResolver(null)
    setOpts(null)
  }

  const open = opts !== null
  const destructive = opts?.destructive ?? false

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog.Root open={open} onOpenChange={(v) => !v && answer(false)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-slate-900/30 backdrop-blur-[1px]" />
          <Dialog.Content
            className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-5 shadow-panel"
            aria-describedby={opts?.description ? 'confirm-desc' : undefined}
          >
            <div className="mb-3 flex items-start gap-3">
              {destructive && (
                <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-red-50 text-red-600">
                  <AlertTriangle size={15} />
                </div>
              )}
              <div className="flex-1">
                <Dialog.Title className="text-sm font-semibold text-slate-800">
                  {opts?.title ?? 'Are you sure?'}
                </Dialog.Title>
                {opts?.description && (
                  <Dialog.Description id="confirm-desc" className="mt-1 text-xs text-slate-500">
                    {opts.description}
                  </Dialog.Description>
                )}
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button className="penser-button-ghost" onClick={() => answer(false)}>
                {opts?.cancelLabel ?? 'Cancel'}
              </button>
              <button
                className={destructive
                  ? 'inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-red-700'
                  : 'penser-button-primary'}
                onClick={() => answer(true)}
                autoFocus
              >
                {opts?.confirmLabel ?? 'Confirm'}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </ConfirmContext.Provider>
  )
}

/** await confirm({ title, description, destructive }) */
export function useConfirm(): ConfirmContextValue {
  const ctx = useContext(ConfirmContext)
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>')
  return ctx
}
